"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkCliVersion = exports.loadWidgetCheck = exports.getWidgetConfig = exports.checkCretInvalid = exports.initializeWidget = exports.loadWidget = exports.WidgetLoadError = void 0;
const loadjs_1 = __importDefault(require("loadjs"));
const axios_1 = __importDefault(require("axios"));
const core_1 = require("@apitable/core");
const componentMap = new Map();
const getDevWidgetHttpOrigin = (bundleUrl) => {
    const url = new URL(bundleUrl);
    const { hostname, port } = url;
    return `http://${hostname}:${Number(port) + 1}`;
};
var WidgetLoadError;
(function (WidgetLoadError) {
    // url dose not meet specifications
    WidgetLoadError[WidgetLoadError["UrlIllegal"] = 1] = "UrlIllegal";
    // packageId not match
    WidgetLoadError[WidgetLoadError["PackageIdNotMatch"] = 2] = "PackageIdNotMatch";
    // browser loading https restriction not lifted
    WidgetLoadError[WidgetLoadError["CretInvalid"] = 3] = "CretInvalid";
    // widget-cli not start
    WidgetLoadError[WidgetLoadError["LoadError"] = 4] = "LoadError";
    // widget-cli too low version
    WidgetLoadError[WidgetLoadError["CliLowVersion"] = 5] = "CliLowVersion";
    // unknown version
    WidgetLoadError[WidgetLoadError["UnknownError"] = -1] = "UnknownError";
})(WidgetLoadError = exports.WidgetLoadError || (exports.WidgetLoadError = {}));
function loadWidget(url, widgetPackageId, refresh) {
    return new Promise((resolve, reject) => {
        const component = componentMap.get(widgetPackageId);
        if (!refresh && component) {
            resolve(component);
            return;
        }
        const requestUrl = refresh ? url + `?v=${Date.now()}` : url;
        componentMap.delete(widgetPackageId);
        (0, loadjs_1.default)(requestUrl, {
            success: () => {
                // after the widget is loaded, it will be triggered an initializeWidget,
                // which is stored in the componentMap and can be retrieved in the next event loop
                setTimeout(() => {
                    const cp = componentMap.get(widgetPackageId);
                    if (!cp) {
                        reject(WidgetLoadError.PackageIdNotMatch);
                        return;
                    }
                    resolve(cp);
                    console.log('widgetPackage: %s loaded', widgetPackageId);
                });
            },
            error: () => __awaiter(this, void 0, void 0, function* () {
                const error = yield checkCretInvalid(url);
                reject(error);
            })
        });
    });
}
exports.loadWidget = loadWidget;
function initializeWidget(Component, widgetPackageId) {
    if (!widgetPackageId) {
        throw Error('widget load error, widgetPackageId is undefined');
    }
    componentMap.set(widgetPackageId, Component);
}
exports.initializeWidget = initializeWidget;
/**
 * distinguish whether the browser restriction is not lifted when the widget loads with an error
 * @param bundleUrl
 */
function checkCretInvalid(bundleUrl) {
    return new Promise((resolve) => {
        const img = document.createElement('img');
        img.src = `${getDevWidgetHttpOrigin(bundleUrl)}/ping.png?v=${Date.now()}`;
        document.body.appendChild(img);
        img.onload = () => {
            var _a;
            resolve(WidgetLoadError.CretInvalid);
            (_a = img.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(img);
        };
        img.onerror = () => {
            var _a;
            resolve(WidgetLoadError.LoadError);
            (_a = img.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(img);
        };
    });
}
exports.checkCretInvalid = checkCretInvalid;
/**
 * get sandbox configuration in widget development mode
 * @param bundleUrl bundleUrl path
 */
function getWidgetConfig(bundleUrl) {
    const url = new URL(bundleUrl);
    return new Promise((resolve, reject) => {
        axios_1.default.get(`${url.origin}/widgetConfig?v=${Date.now()}`)
            .then(res => resolve(res.data))
            .catch(() => __awaiter(this, void 0, void 0, function* () {
            /**
             * reasons for loading failure
             * 1. service is not available
             * 2. browser restrictions are not lifted
             */
            const error = yield checkCretInvalid(bundleUrl);
            reject(error);
        }));
    });
}
exports.getWidgetConfig = getWidgetConfig;
/**
 * check for widget dev mode loading
 * @param bundleUrl path of the widget bundleUrl
 * @param widgetPackageId
 */
function loadWidgetCheck(bundleUrl, widgetPackageId) {
    return new Promise((resolve, reject) => {
        checkCliVersion(bundleUrl).then(() => {
            getWidgetConfig(bundleUrl).then(res => {
                if (res.packageId === widgetPackageId) {
                    resolve(res);
                }
                else {
                    reject(WidgetLoadError.PackageIdNotMatch);
                }
            }).catch(reject);
        }).catch(reject);
    });
}
exports.loadWidgetCheck = loadWidgetCheck;
/**
 * check version method
 * a > b return 1
 * a = b return 0
 * a < b return -1
 * @param a
 * @param b
 */
function checkVersion(a, b) {
    const x = a.split('.').map(e => parseInt(e, 10));
    const y = b.split('.').map(e => parseInt(e, 10));
    for (const i in x) {
        y[i] = y[i] || 0;
        if (x[i] === y[i]) {
            continue;
        }
        else if (x[i] > y[i]) {
            return 1;
        }
        else {
            return -1;
        }
    }
    return y.length > x.length ? -1 : 0;
}
/**
 * cli version check
 */
function checkCliVersion(bundleUrl) {
    return new Promise((resolve, reject) => {
        axios_1.default.get(`${getDevWidgetHttpOrigin(bundleUrl)}/widget-cli/info?v=${Date.now()}`)
            .then(res => {
            const cliInfo = res.data;
            const minSupportVersion = core_1.SystemConfig.settings.widget_cli_miumum_version.value;
            if (checkVersion(cliInfo.version, minSupportVersion) === -1) {
                reject(WidgetLoadError.CliLowVersion);
                return;
            }
            resolve(cliInfo);
        })
            .catch(() => {
            reject(WidgetLoadError.LoadError);
        });
    });
}
exports.checkCliVersion = checkCliVersion;
